import type { Permission } from '@qualicloud/contracts';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { Button, Card, EmptyState, IconLock, SkeletonList } from '@/design-system';
import { useAuth } from '@/providers/auth-provider';

export interface ProtectedRouteProps {
  readonly children: ReactNode;
  /** Todas as permissões exigidas. Vazio = basta estar autenticado. */
  readonly permissions?: readonly Permission[];
  /** `any` exige uma das permissões; `all` exige todas. Padrão: `any`. */
  readonly mode?: 'any' | 'all';
}

/**
 * Guarda de rota por permissão.
 *
 * IMPORTANTE: isto é USABILIDADE, não segurança. Esconder um botão não protege
 * nada — a autorização real está no `PermissionsGuard` do NestJS e na RLS do
 * PostgreSQL, e o cliente jamais é a fonte da verdade. O papel desta guarda é
 * evitar que o usuário navegue até uma tela que só lhe devolveria 403.
 *
 * Quando falta permissão mostramos uma tela EXPLICATIVA em vez de redirecionar
 * em silêncio: sumir com a página deixa o usuário achando que o link está
 * quebrado e gera chamado. Dizer "falta a permissão X, procure o administrador"
 * resolve.
 */
export function ProtectedRoute({ children, permissions = [], mode = 'any' }: ProtectedRouteProps) {
  const { status, user, can } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === 'anonymous') {
      void navigate({ to: '/login', search: { redirect: window.location.pathname } });
    }
  }, [status, navigate]);

  if (status === 'booting') {
    return (
      <div className="p-6" aria-busy="true" aria-live="polite">
        <span className="qc-sr-only">Verificando sua sessão…</span>
        <SkeletonList rows={6} />
      </div>
    );
  }

  if (status === 'anonymous' || !user) {
    return null;
  }

  // §11.300 / política de senha: enquanto a troca for obrigatória, nenhuma tela
  // de negócio é acessível. Uma senha expirada não pode assinar registro.
  if (user.mustChangePassword && window.location.pathname !== '/trocar-senha') {
    return <RedirectToPasswordChange />;
  }

  const allowed =
    permissions.length === 0 ||
    (mode === 'all' ? permissions.every(can) : permissions.some(can));

  if (!allowed) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <Card>
          <EmptyState
            icon={<IconLock size={22} />}
            title="Acesso não autorizado"
            description="Você não tem permissão para acessar esta área. Se acredita que deveria ter, procure o administrador da sua organização informando qual tela precisa acessar."
            action={
              <Button asChild variant="primary">
                <Link to="/">Voltar ao painel</Link>
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

function RedirectToPasswordChange() {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate({ to: '/trocar-senha' });
  }, [navigate]);
  return null;
}

/**
 * Renderiza o filho apenas quando a permissão existe.
 * Usado para esconder ações dentro de uma tela já acessível.
 */
export function IfPermitted({
  permission,
  children,
  fallback = null,
}: {
  readonly permission: Permission;
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}) {
  const { can } = useAuth();
  return <>{can(permission) ? children : fallback}</>;
}
